import { useQuery } from "@tanstack/react-query";
import { Activity, ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { api, type Job } from "@/lib/api";
import { fmtAgo } from "@/lib/utils";

const VARIANT: Record<string, "success" | "warning" | "destructive" | "secondary" | "default"> = {
  done: "success", running: "warning", queued: "secondary", leased: "warning",
  failed: "destructive", cancelled: "secondary",
};

export default function Jobs() {
  const [openId, setOpenId] = useState<string | null>(null);
  const { data } = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get<{ items: Job[] }>("/api/jobs?limit=100").then((r) => r.items),
    refetchInterval: 8_000,
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Jobs"
        description="Durable queue — applies survive controller restarts; expired leases are recovered by other workers. A terminal failure never starves the queue."
      />
      <Card>
        <Table>
          <THead>
            <TR><TH className="w-6"></TH><TH>Type</TH><TH>Status</TH><TH>Attempts</TH><TH>Key</TH><TH>Queued</TH></TR>
          </THead>
          <TBody>
            {(data ?? []).map((j) => {
              const open = openId === j.id;
              const hasDetail = Object.keys(j.progress ?? {}).length > 0 || j.error;
              return (
                <Fragment key={j.id}>
                  <TR className={hasDetail ? "cursor-pointer" : ""}
                    onClick={() => hasDetail && setOpenId(open ? null : j.id)}>
                    <TD className="w-6 px-2 text-muted-foreground">
                      {hasDetail && (open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)}
                    </TD>
                    <TD className="font-mono text-xs">{j.type}</TD>
                    <TD><Badge variant={VARIANT[j.status] ?? "secondary"}>{j.status}</Badge></TD>
                    <TD className="num text-xs">{j.attempts}</TD>
                    <TD className="max-w-[220px] truncate font-mono text-xs text-muted-foreground">
                      {j.idempotency_key}
                    </TD>
                    <TD className="text-xs text-muted-foreground">{fmtAgo(j.created_at)}</TD>
                  </TR>
                  {open && hasDetail && (
                    <TR>
                      <TD colSpan={6} className="bg-muted/30 px-6 py-3">
                        {j.error && <div className="mb-2 text-xs text-destructive">{j.error}</div>}
                        <pre className="max-h-48 overflow-auto font-mono text-[11px] text-muted-foreground">
                          {JSON.stringify(j.progress, null, 2)}
                        </pre>
                      </TD>
                    </TR>
                  )}
                </Fragment>
              );
            })}
          </TBody>
        </Table>
        {(data ?? []).length === 0 && (
          <EmptyState icon={Activity} title="No jobs yet"
            hint="Applies, exception syncs and periodic collectors appear here." />
        )}
      </Card>
    </div>
  );
}
